import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { 
  Save, RotateCcw, Eye, Loader, Palette, Image, Type,
  Upload, CheckCircle, AlertTriangle, Sparkles, Layout,
  X, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Componente de preview da loja
const StorePreview = ({ theme }) => {
  const t = theme || {};
  
  return (
    <div 
      className="rounded-xl overflow-hidden border-2 border-gray-200 shadow-lg"
      style={{ backgroundColor: t.background_color || '#ffffff' }}
    >
      {/* Banner */}
      {t.show_banner !== false && (
        <div 
          className="h-32 bg-gradient-to-r from-indigo-500 to-purple-500 relative"
          style={t.banner_image ? { 
            backgroundImage: `url(${t.banner_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {
            background: `linear-gradient(to right, ${t.primary_color || '#4f46e5'}, ${t.secondary_color || '#7c3aed'})`
          }}
        >
          {/* Profile */}
          {t.show_profile !== false && (
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
              <div 
                className="w-16 h-16 rounded-full border-4 border-white bg-gray-200 overflow-hidden"
                style={{ backgroundColor: t.card_color || '#ffffff' }}
              >
                {t.profile_image ? (
                  <img src={t.profile_image} alt="Perfil" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Store Name */}
      <div className={`text-center ${t.show_banner !== false ? 'pt-12' : 'pt-6'} pb-4`}>
        <h2 
          className="text-xl font-bold"
          style={{ color: t.text_color || '#1f2937' }}
        >
          {t.store_name || 'Minha Loja'}
        </h2>
      </div>
      
      {/* Sample Products */}
      <div className="px-4 pb-4 grid grid-cols-2 gap-3">
        {[1, 2].map((i) => (
          <div 
            key={i}
            className={`${t.card_style === 'square' ? 'rounded-none' : t.card_style === 'minimal' ? 'rounded-sm' : 'rounded-lg'} overflow-hidden shadow-sm border`}
            style={{ 
              backgroundColor: t.card_color || '#ffffff',
              borderColor: t.card_color === '#ffffff' ? '#e5e7eb' : 'transparent'
            }}
          >
            <div className="h-20 bg-gray-200" />
            <div className="p-2">
              <p 
                className="text-xs font-medium truncate"
                style={{ color: t.card_text_color || '#1f2937' }}
              >
                Produto Exemplo {i}
              </p>
              <p 
                className="text-sm font-bold"
                style={{ color: t.primary_color || '#4f46e5' }}
              >
                R$ 99,90
              </p>
              <button
                className="w-full mt-2 py-1 rounded text-xs font-medium"
                style={{ 
                  backgroundColor: t.button_color || '#4f46e5',
                  color: t.button_text_color || '#ffffff'
                }}
              >
                Comprar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Componente de cor com validação
const ColorInput = ({ label, value, onChange, contrastWith, description }) => {
  const [contrast, setContrast] = useState(null);
  
  useEffect(() => {
    if (contrastWith && value) {
      // Calcular contraste simples
      const getL = (hex) => {
        const rgb = parseInt(hex.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >> 8) & 0xff;
        const b = rgb & 0xff;
        return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      };
      
      const l1 = getL(value || '#000000');
      const l2 = getL(contrastWith || '#ffffff');
      const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
      
      setContrast({
        ratio: ratio.toFixed(1),
        good: ratio >= 4.5
      });
    }
  }, [value, contrastWith]);
  
  return (
    <div>
      <Label className="flex items-center gap-2">
        {label}
        {contrast && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${contrast.good ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {contrast.ratio}:1
          </span>
        )}
      </Label>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
        />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="flex-1 font-mono text-sm"
        />
      </div>
      {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
      {contrast && !contrast.good && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Contraste baixo - pode dificultar leitura
        </p>
      )}
    </div>
  );
};

export const StoreThemeEditor = () => {
  const { user } = useAuth();
  const [theme, setTheme] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [autoFix, setAutoFix] = useState(true);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiVibe, setAiVibe] = useState(null);
  const [aiReason, setAiReason] = useState(null);

  const fetchTheme = useCallback(async () => {
    try {
      const [themeRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/seller/store-theme`, { withCredentials: true }),
        axios.get(`${API_URL}/seller/store-theme/templates`, { withCredentials: true })
      ]);
      setTheme(themeRes.data);
      setTemplates(templatesRes.data.templates || []);
      setHasChanges(false);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);

  const handleChange = (field, value) => {
    setTheme(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/seller/store-theme?auto_fix=${autoFix}`,
        theme,
        { withCredentials: true }
      );
      
      if (response.data.auto_fixed && response.data.validation?.validations) {
        const fixes = Object.values(response.data.validation.validations)
          .filter(v => v.needs_adjustment);
        if (fixes.length > 0) {
          toast.info('Algumas cores foram ajustadas automaticamente para melhor contraste');
        }
      }
      
      setTheme(response.data.theme);
      toast.success('Vitrine personalizada com sucesso!');
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restaurar configurações padrão? Você perderá todas as personalizações.')) {
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API_URL}/seller/store-theme/reset`,
        {},
        { withCredentials: true }
      );
      setTheme(response.data.theme);
      toast.success('Tema resetado!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Erro ao resetar');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${API_URL}/seller/store-theme/template/${templateId}`,
        {},
        { withCredentials: true }
      );
      setTheme(response.data.theme);
      toast.success('Template aplicado!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Erro ao aplicar template');
    } finally {
      setSaving(false);
    }
  };

  const handleAiGenerate = async () => {
    setAiGenerating(true);
    setAiVibe(null);
    setAiReason(null);
    try {
      const response = await axios.post(
        `${API_URL}/seller/store-theme/ai-generate`,
        {},
        { withCredentials: true, timeout: 45000 }
      );
      if (response.data.theme) {
        setTheme(response.data.theme);
        setAiVibe(response.data.vibe || null);
        setAiReason(response.data.reason || null);
        setHasChanges(false);
        toast.success('Vitrine exclusiva gerada pela IA e aplicada com sucesso!');
      } else {
        toast.error(response.data.error || 'Falha ao gerar tema por IA');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar tema por IA');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleImageUpload = async (field, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 5MB.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/upload/product-image`,
        formData,
        { withCredentials: true }
      );
      
      handleChange(field, response.data.url);
      toast.success('Imagem enviada!');
    } catch (error) {
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (field) => {
    handleChange(field, null);
  };

  const openPreview = () => {
    if (user?.store_id) {
      window.open(`/loja/${user.store_id}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="flex justify-center py-20">
          <Loader className="w-12 h-12 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="store-theme-title">
              Personalizar Vitrine
            </h1>
            <p className="text-lg text-gray-600">
              Defina cores, imagens e identidade da sua loja
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={openPreview}
              data-testid="preview-store-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Ver Loja
            </Button>
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={saving}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Resetar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-theme-btn"
            >
              {saving ? (
                <Loader className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar
            </Button>
          </div>
        </div>

        {hasChanges && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="text-amber-800 text-sm">Alterações não salvas</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-amber-700">Auto-corrigir contraste</span>
              <Switch
                checked={autoFix}
                onCheckedChange={setAutoFix}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configurações */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="identity" className="space-y-6">
              <TabsList className="bg-white border border-gray-200 p-1">
                <TabsTrigger value="identity" className="data-[state=active]:bg-indigo-100">
                  <Type className="w-4 h-4 mr-2" />
                  Identidade
                </TabsTrigger>
                <TabsTrigger value="images" className="data-[state=active]:bg-indigo-100">
                  <Image className="w-4 h-4 mr-2" />
                  Imagens
                </TabsTrigger>
                <TabsTrigger value="colors" className="data-[state=active]:bg-indigo-100">
                  <Palette className="w-4 h-4 mr-2" />
                  Cores
                </TabsTrigger>
                <TabsTrigger value="layout" className="data-[state=active]:bg-indigo-100">
                  <Layout className="w-4 h-4 mr-2" />
                  Layout
                </TabsTrigger>
                <TabsTrigger value="templates" className="data-[state=active]:bg-indigo-100">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Templates
                </TabsTrigger>
              </TabsList>

              {/* Identidade */}
              <TabsContent value="identity" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Identidade da Loja</h3>
                
                <div>
                  <Label>Nome da Vitrine</Label>
                  <Input
                    value={theme?.store_name || ''}
                    onChange={(e) => handleChange('store_name', e.target.value)}
                    placeholder="Minha Loja"
                    maxLength={50}
                    data-testid="store-name-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Aparece centralizado no topo da sua vitrine
                  </p>
                </div>
              </TabsContent>

              {/* Imagens */}
              <TabsContent value="images" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Imagens</h3>
                
                <div className="space-y-6">
                  {/* Banner */}
                  <div>
                    <Label>Banner (Capa)</Label>
                    <div className="mt-2">
                      {theme?.banner_image ? (
                        <div className="relative w-full h-32 rounded-lg overflow-hidden">
                          <img 
                            src={theme.banner_image} 
                            alt="Banner" 
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => removeImage('banner_image')}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-colors">
                          {uploading ? (
                            <Loader className="w-8 h-8 animate-spin text-indigo-600" />
                          ) : (
                            <>
                              <Upload className="w-8 h-8 text-gray-400 mb-2" />
                              <span className="text-sm text-gray-600">Clique para enviar banner</span>
                              <span className="text-xs text-gray-400">Recomendado: 1200x300px</span>
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload('banner_image', e)}
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Perfil */}
                  <div>
                    <Label>Foto de Perfil</Label>
                    <div className="mt-2 flex items-center gap-4">
                      {theme?.profile_image ? (
                        <div className="relative">
                          <img 
                            src={theme.profile_image} 
                            alt="Perfil" 
                            className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                          />
                          <button
                            onClick={() => removeImage('profile_image')}
                            className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-20 h-20 border-2 border-dashed border-gray-300 rounded-full cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-colors">
                          {uploading ? (
                            <Loader className="w-6 h-6 animate-spin text-indigo-600" />
                          ) : (
                            <Upload className="w-6 h-6 text-gray-400" />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload('profile_image', e)}
                            disabled={uploading}
                          />
                        </label>
                      )}
                      <p className="text-sm text-gray-500">Recomendado: 200x200px</p>
                    </div>
                  </div>

                  {/* Fundo */}
                  <div>
                    <Label>Imagem de Fundo (opcional)</Label>
                    <div className="mt-2">
                      {theme?.background_image ? (
                        <div className="relative w-full h-24 rounded-lg overflow-hidden">
                          <img 
                            src={theme.background_image} 
                            alt="Fundo" 
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => removeImage('background_image')}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-colors">
                          {uploading ? (
                            <Loader className="w-6 h-6 animate-spin text-indigo-600" />
                          ) : (
                            <>
                              <Upload className="w-6 h-6 text-gray-400 mb-1" />
                              <span className="text-xs text-gray-600">Imagem de fundo</span>
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload('background_image', e)}
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Cores */}
              <TabsContent value="colors" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Cores</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ColorInput
                    label="Cor Principal"
                    value={theme?.primary_color}
                    onChange={(v) => handleChange('primary_color', v)}
                    description="Usada em destaques e preços"
                  />
                  
                  <ColorInput
                    label="Cor Secundária"
                    value={theme?.secondary_color}
                    onChange={(v) => handleChange('secondary_color', v)}
                    description="Usada em gradientes"
                  />
                  
                  <ColorInput
                    label="Cor de Fundo"
                    value={theme?.background_color}
                    onChange={(v) => handleChange('background_color', v)}
                    description="Fundo da página"
                  />
                  
                  <ColorInput
                    label="Cor do Texto"
                    value={theme?.text_color}
                    onChange={(v) => handleChange('text_color', v)}
                    contrastWith={theme?.background_color}
                    description="Textos sobre o fundo"
                  />
                  
                  <ColorInput
                    label="Cor dos Botões"
                    value={theme?.button_color}
                    onChange={(v) => handleChange('button_color', v)}
                    description="Fundo dos botões"
                  />
                  
                  <ColorInput
                    label="Texto dos Botões"
                    value={theme?.button_text_color}
                    onChange={(v) => handleChange('button_text_color', v)}
                    contrastWith={theme?.button_color}
                    description="Texto sobre os botões"
                  />
                  
                  <ColorInput
                    label="Cor dos Cards"
                    value={theme?.card_color}
                    onChange={(v) => handleChange('card_color', v)}
                    description="Fundo dos cards de produto"
                  />
                  
                  <ColorInput
                    label="Texto dos Cards"
                    value={theme?.card_text_color}
                    onChange={(v) => handleChange('card_text_color', v)}
                    contrastWith={theme?.card_color}
                    description="Texto sobre os cards"
                  />
                </div>
              </TabsContent>

              {/* Layout */}
              <TabsContent value="layout" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Layout</h3>
                
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Mostrar Banner</p>
                      <p className="text-sm text-gray-500">Exibir imagem de capa no topo</p>
                    </div>
                    <Switch
                      checked={theme?.show_banner !== false}
                      onCheckedChange={(v) => handleChange('show_banner', v)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Mostrar Foto de Perfil</p>
                      <p className="text-sm text-gray-500">Exibir logo/avatar da loja</p>
                    </div>
                    <Switch
                      checked={theme?.show_profile !== false}
                      onCheckedChange={(v) => handleChange('show_profile', v)}
                    />
                  </div>
                  
                  <div>
                    <Label>Produtos por Linha</Label>
                    <div className="flex gap-2 mt-2">
                      {[2, 3, 4].map((n) => (
                        <button
                          key={n}
                          onClick={() => handleChange('products_per_row', n)}
                          className={`flex-1 py-2 rounded-lg border-2 transition-colors ${
                            (theme?.products_per_row || 3) === n
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Estilo dos Cards</Label>
                    <div className="flex gap-2 mt-2">
                      {[
                        { id: 'rounded', label: 'Arredondado' },
                        { id: 'square', label: 'Quadrado' },
                        { id: 'minimal', label: 'Minimalista' }
                      ].map((style) => (
                        <button
                          key={style.id}
                          onClick={() => handleChange('card_style', style.id)}
                          className={`flex-1 py-2 rounded-lg border-2 transition-colors ${
                            (theme?.card_style || 'rounded') === style.id
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {style.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Templates */}
              <TabsContent value="templates" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                {/* CARD PREMIUM — GERAR COM IA */}
                <div
                  className="relative overflow-hidden rounded-2xl p-6 mb-8 border"
                  style={{
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #4c1d95 100%)',
                    borderColor: 'rgba(255,255,255,0.15)'
                  }}
                >
                  {/* Ornamento */}
                  <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-25 blur-3xl" style={{ background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)' }} />
                  <div className="absolute -bottom-20 -left-16 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: 'radial-gradient(circle, #a855f7 0%, transparent 70%)' }} />

                  <div className="relative z-10">
                    <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
                      <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-6 h-6 text-amber-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] font-bold tracking-widest uppercase text-amber-300 mb-3">
                          <Sparkles className="w-3 h-3" />
                          Exclusivo
                        </div>
                        <h3 className="text-2xl font-bold text-white leading-tight mb-1.5">
                          Personalize sua vitrine com IA
                        </h3>
                        <p className="text-white/70 text-sm leading-relaxed max-w-lg">
                          Nossa IA analisa seu segmento, produtos e identidade para criar uma vitrine
                          <span className="text-white font-semibold"> única</span>, inspirada em grandes e-commerces —
                          cores, tipografia visual e composição sob medida para seu negócio.
                        </p>

                        {/* Feature pills */}
                        <div className="flex flex-wrap gap-2 mt-4">
                          {['Paleta exclusiva', 'Contraste WCAG', 'Layout inteligente', 'Baseado no seu nicho'].map((f) => (
                            <span key={f} className="text-[11px] px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-white/85 font-medium">
                              {f}
                            </span>
                          ))}
                        </div>

                        <Button
                          onClick={handleAiGenerate}
                          disabled={aiGenerating || saving}
                          className="mt-5 font-semibold rounded-full px-6 shadow-xl hover:scale-105 transition-transform disabled:opacity-60 disabled:hover:scale-100"
                          style={{
                            background: 'linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)',
                            color: '#ffffff',
                            border: 'none'
                          }}
                          data-testid="ai-generate-theme-btn"
                        >
                          {aiGenerating ? (
                            <>
                              <Loader className="w-4 h-4 mr-2 animate-spin" />
                              Criando sua vitrine exclusiva...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Personalizar com IA
                            </>
                          )}
                        </Button>

                        {/* Resultado */}
                        {(aiVibe || aiReason) && !aiGenerating && (
                          <div className="mt-5 p-4 rounded-xl bg-white/5 border border-white/10" data-testid="ai-theme-result">
                            {aiVibe && (
                              <div className="text-amber-300 text-xs font-bold tracking-widest uppercase mb-1">
                                Estética criada
                              </div>
                            )}
                            {aiVibe && <div className="text-white font-semibold mb-2">{aiVibe}</div>}
                            {aiReason && <div className="text-white/70 text-sm leading-relaxed">{aiReason}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ou escolha um Template Pronto</h3>
                <p className="text-sm text-gray-600 mb-6">Comece por um tema base e refine depois nas outras abas</p>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleApplyTemplate(template.id)}
                      disabled={saving}
                      className="p-4 rounded-xl border-2 border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all text-left"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div 
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: template.primary_color }}
                        />
                        <div 
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: template.secondary_color }}
                        />
                        <div 
                          className="w-6 h-6 rounded-full border"
                          style={{ backgroundColor: template.background_color }}
                        />
                      </div>
                      <p className="font-medium text-gray-900">{template.name}</p>
                    </button>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Preview */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Preview em Tempo Real
              </h3>
              <StorePreview theme={theme} />
              <p className="text-xs text-gray-500 text-center mt-4">
                Visualização aproximada da sua vitrine
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
